import { IconEye } from "@/assets/icons/geist/IconEye";
import { IconEyeOff } from "@/assets/icons/geist/IconEyeOff";
import { Button } from "@/components/flexkit/Button";
import { Input } from "@/components/podkit/forms/Input";
import { useTemporaryState } from "@/hooks/use-temporary-value";
import { Check, Copy } from "lucide-react";
import { useCallback, useState, type FC } from "react";

type SCIMTokenInputProps = {
    value: string;
    copyTrackingId?: string;
    revealTrackingId?: string;
};

export const SCIMTokenInput: FC<SCIMTokenInputProps> = ({
    value,
    copyTrackingId = "copy-scim-token",
    revealTrackingId = "reveal-scim-token",
}) => {
    const [copied, setCopied] = useTemporaryState(false, 2000);
    const [isVisible, setIsVisible] = useState(false);

    const handleCopyToClipboard = useCallback(() => {
        if (!value) return;
        void navigator.clipboard.writeText(value).then(() => {
            setCopied(true);
        });
    }, [setCopied, value]);

    const handleToggleVisibility = useCallback(() => {
        setIsVisible((current) => !current);
    }, []);

    return (
        <div className="relative">
            <Input className="pr-16 ring-inset" readOnly value={value} type={isVisible ? "text" : "password"} />
            <div className="absolute inset-y-0 right-2 flex items-center gap-1">
                <Button
                    variant="ghost"
                    type="button"
                    className="h-6 rounded-lg border-none p-1 text-content-tertiary hover:text-content-secondary hover:opacity-100"
                    onClick={handleToggleVisibility}
                    aria-label={isVisible ? "Hide token" : "Show token"}
                    data-tracking-id={revealTrackingId}
                    disabled={!value}
                >
                    {isVisible ? <IconEyeOff size="sm" /> : <IconEye size="sm" />}
                </Button>
                <Button
                    variant="ghost"
                    type="button"
                    className="h-6 rounded-lg border-none p-1 text-content-tertiary hover:text-content-secondary hover:opacity-100"
                    onClick={handleCopyToClipboard}
                    aria-label="Copy token"
                    data-tracking-id={copyTrackingId}
                    disabled={!value}
                >
                    {copied ? (
                        <Check className="text-content-success" aria-hidden size={16} />
                    ) : (
                        <Copy aria-hidden size={16} />
                    )}
                </Button>
            </div>
        </div>
    );
};
